/**
 * The Gauge's editor in the Plot card: a list of dials, one per sensor.
 *
 * Each dial is a Number's value - the same Signal / Calculation, formula
 * editor, Label, Unit and Decimal places - with a scale and zones beside it,
 * all in view (nothing under Advanced). Changing what a dial reads waits for
 * Plot; everything else redraws what is loaded. A dial's card is picked up by
 * its handle and follows the pointer to its new place.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { allColumns, derivedSignals } from '../../state/store.ts';
import {
  addGaugeDial,
  duplicateGaugeDial,
  moveGaugeDial,
  plotState,
  removeGaugeDial,
  setGaugeDial,
} from '../../state/plot-state.ts';
import { lastPlotData } from './plot-ui-state.ts';
import { plotColumnPool } from '../ship/ship-columns.ts';
import { isNumericType } from '../../utils/columns.ts';
import type { ColumnInfo, NumberAgg } from '../../api/types.ts';
import { NUMBER_AGGS } from './number-config.ts';
import {
  GAUGE_MAX_DIALS,
  GAUGE_STEP_CHOICES,
  autoStepCount,
  decimalsOf,
  dialRange,
  dialTitle,
  gaugeScale,
  type GaugeDialState,
  type GaugeZone,
} from './gauge-config.ts';
import { readRows } from './render/render-gauge.ts';
import '../../ui/column-combobox.ts';
import '../../ui/plain-select.ts';
import '../../ui/unit-picker.ts';
import '../../ui/number-formula-editor.ts';

const DECIMALS = Array.from({ length: 11 }, (_, i) => ({ value: String(i), label: String(i) }));
const ZONE_COLORS = [
  { value: 'yellow', label: 'Yellow' },
  { value: 'red', label: 'Red' },
];

/** A typed number, or null for an empty box. */
function parseNum(raw: string): number | null {
  if (!raw.trim()) return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

const fmt = (v: number): string => new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(v);

@customElement('gauge-controls')
export class GaugeControls extends LightElement {
  private store = new StoreController(this, [plotState, allColumns, derivedSignals, lastPlotData]);

  /** The dial whose settings are open; -1 for none. */
  @state() private open = 0;

  private numericPool(): ColumnInfo[] {
    return plotColumnPool().filter((c) => isNumericType(c.type) || c.derived);
  }

  /** The loaded values for a dial, across ships, for Auto ranges and steps. */
  private loadedValues(index: number): Array<number | null> {
    const data = lastPlotData.value;
    if (data?.plot_type !== 'gauge') return [];
    const dials = plotState.value.gauge_dials;
    return readRows(data, dials).map((r) => r.readings[index]?.value ?? null);
  }

  /* ── Reorder: the card follows the pointer ─────────────── */

  /** Keep the open card open wherever a move takes it. */
  private followOpen(from: number, to: number): void {
    if (this.open === from) this.open = to;
    else if (from < this.open && to >= this.open) this.open--;
    else if (from > this.open && to <= this.open) this.open++;
  }

  /**
   * Pick a card up by its handle: it moves with the pointer, the cards it
   * passes slide out of its way, and on release it settles into the gap. Not
   * HTML5 drag and drop, whose ghost image stays behind the pointer instead of
   * the card itself.
   */
  private onGrab(e: PointerEvent, from: number): void {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const list = this.querySelector<HTMLElement>('.gauge-dials');
    const cards = [...(list?.children ?? [])] as HTMLElement[];
    const lifted = cards[from];
    if (!list || !lifted || cards.length < 2) return;
    const rects = cards.map((c) => c.getBoundingClientRect());
    const gap = rects.length > 1 ? Math.max(0, rects[1]!.top - rects[0]!.bottom) : 0;
    const span = rects[from]!.height + gap;
    const handle = e.currentTarget as HTMLElement;
    try { handle.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
    list.classList.add('is-sorting');
    lifted.classList.add('is-lifted');
    const startY = e.clientY;
    const minDy = rects[0]!.top - rects[from]!.top;
    const maxDy = rects.at(-1)!.bottom - rects[from]!.bottom;
    let dy = 0;
    let to = from;
    const move = (ev: PointerEvent): void => {
      dy = Math.min(maxDy, Math.max(minDy, ev.clientY - startY));
      lifted.style.transform = `translateY(${dy}px)`;
      const centre = rects[from]!.top + rects[from]!.height / 2 + dy;
      to = from;
      for (let j = from + 1; j < rects.length; j++) if (centre > rects[j]!.top + rects[j]!.height / 2) to = j;
      for (let j = from - 1; j >= 0; j--) if (centre < rects[j]!.top + rects[j]!.height / 2) to = j;
      cards.forEach((card, j) => {
        if (j === from) return;
        const shift = to > from && j > from && j <= to ? -span : to < from && j < from && j >= to ? span : 0;
        card.style.transform = shift ? `translateY(${shift}px)` : '';
      });
    };
    const up = async (ev: PointerEvent): Promise<void> => {
      if (handle.hasPointerCapture(ev.pointerId)) handle.releasePointerCapture(ev.pointerId);
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      // Where the card is on screen now, measured from the slot it lands in.
      const landedTop = rects[from]!.top + dy;
      const slotTop = to === from ? rects[from]!.top
        : to > from ? rects[to]!.bottom - rects[from]!.height : rects[to]!.top;
      list.classList.remove('is-sorting');
      cards.forEach((card) => { card.style.transform = ''; card.classList.remove('is-lifted'); });
      if (to !== from) {
        this.followOpen(from, to);
        moveGaugeDial(from, to);
        await this.updateComplete;
      }
      // Settle: start the card where it was let go and ease it into its slot.
      const settled = ([...list.children] as HTMLElement[])[to];
      if (!settled || Math.abs(landedTop - slotTop) < 1) return;
      settled.style.transition = 'none';
      settled.style.transform = `translateY(${landedTop - slotTop}px)`;
      void settled.offsetHeight;
      settled.style.transition = '';
      settled.classList.add('is-settling');
      settled.style.transform = '';
      settled.addEventListener('transitionend', () => settled.classList.remove('is-settling'), { once: true });
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  }

  /** Alt+Up / Alt+Down on a card's header moves it one place. */
  private onMoveKey(e: KeyboardEvent, i: number, count: number): boolean {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return false;
    e.preventDefault();
    const to = e.key === 'ArrowUp' ? i - 1 : i + 1;
    if (to < 0 || to >= count) return true;
    this.followOpen(i, to);
    moveGaugeDial(i, to);
    void this.updateComplete.then(() =>
      this.querySelectorAll<HTMLElement>('.gauge-dial-head')[to]?.focus());
    return true;
  }

  /* ── One dial ─────────────────────────────────────────── */

  private summary(d: GaugeDialState, i: number): TemplateResult {
    const what = d.mode === 'formula' ? 'calculation' : `${d.column || 'no signal'} · ${NUMBER_AGGS.find((a) => a.value === d.agg)?.label.toLowerCase() ?? d.agg}`;
    const range = dialRange(d, this.loadedValues(i));
    const scale = d.min == null && d.max == null && !this.loadedValues(i).length
      ? 'auto scale'
      : range ? `${fmt(range.min)}–${fmt(range.max)}${d.unit ? ` ${d.unit}` : ''}` : 'min above max';
    return html`<span class="gauge-dial-sum">${what} · ${scale}${d.zones.length
      ? html` <span class="gauge-zone-chips">${d.zones.map((z) => html`<i class="is-${z.color}"></i>`)}</span>`
      : nothing}</span>`;
  }

  private renderZone(i: number, d: GaugeDialState, z: GaugeZone, j: number): TemplateResult {
    const setZone = (patch: Partial<GaugeZone>): void =>
      setGaugeDial(i, { zones: d.zones.map((x, k) => (k === j ? { ...x, ...patch } : x)) });
    return html`<div class="gauge-zone">
      <span class="gauge-zone-dot is-${z.color}"></span>
      <plain-select class="gauge-zone-color" .value=${z.color} .options=${ZONE_COLORS}
        .select=${(v: string) => setZone({ color: v as GaugeZone['color'] })}></plain-select>
      <input type="number" step="any" placeholder="Min" aria-label="Zone from" .value=${z.from == null ? '' : String(z.from)}
        @input=${(e: Event) => setZone({ from: parseNum((e.target as HTMLInputElement).value) })} />
      <input type="number" step="any" placeholder="Max" aria-label="Zone to" .value=${z.to == null ? '' : String(z.to)}
        @input=${(e: Event) => setZone({ to: parseNum((e.target as HTMLInputElement).value) })} />
      <button class="remove-btn" title="Remove this zone"
        @click=${() => setGaugeDial(i, { zones: d.zones.filter((_, k) => k !== j) })}>×</button>
    </div>`;
  }

  private renderBody(d: GaugeDialState, i: number): TemplateResult {
    const formula = d.mode === 'formula';
    const values = this.loadedValues(i);
    const range = dialRange(d, values);
    const known = range && (values.some((v) => v != null) || (d.min != null && d.max != null));
    const autoN = known ? autoStepCount(range.min, range.max) : null;
    const scale = known ? gaugeScale(range.min, range.max, d.steps) : null;
    const unit = d.unit.trim() ? ` ${d.unit.trim()}` : '';
    const stepOptions = [
      { value: '', label: autoN ? `Auto (${autoN})` : 'Auto' },
      ...GAUGE_STEP_CHOICES.map((n) => ({ value: String(n), label: String(n) })),
    ];
    const addZone = (): void => {
      const lo = range?.min ?? 0;
      const span = (range?.max ?? 100) - lo;
      const round = (v: number): number => +v.toPrecision(3);
      setGaugeDial(i, { zones: [...d.zones, { color: d.zones.some((z) => z.color === 'yellow') ? 'red' : 'yellow', from: round(lo + span * 0.8), to: round(lo + span * 0.9) }] });
    };
    return html`<div class="gauge-dial-body">
      <div class="seg">
        <button type="button" class="seg-btn ${formula ? '' : 'active'}" aria-pressed=${!formula}
          @click=${() => setGaugeDial(i, { mode: 'signal' })}>Signal</button>
        <button type="button" class="seg-btn ${formula ? 'active' : ''}" aria-pressed=${formula}
          @click=${() => setGaugeDial(i, { mode: 'formula' })}>Calculation</button>
      </div>
      ${formula
        ? html`<number-formula-editor .value=${d.expression} .columns=${this.numericPool()}
            .change=${(value: string) => setGaugeDial(i, { expression: value })}></number-formula-editor>`
        : html`
          <div class="gauge-field">
            <label>Signal</label>
            <column-combobox .columns=${this.numericPool()} .value=${d.column} placeholder="Choose signal…"
              .select=${(v: string) => setGaugeDial(i, { column: v })}></column-combobox>
          </div>
          <div class="gauge-field">
            <label>Calculation</label>
            <plain-select .value=${d.agg} .options=${NUMBER_AGGS}
              .select=${(v: string) => setGaugeDial(i, { agg: v as NumberAgg })}></plain-select>
          </div>`}
      <div class="gauge-field">
        <label for="gauge-label-${i}">Label</label>
        <input type="text" id="gauge-label-${i}" maxlength="120" placeholder="No label" .value=${d.label}
          @input=${(e: Event) => setGaugeDial(i, { label: (e.target as HTMLInputElement).value })} />
      </div>
      <div class="gauge-pair">
        <div class="gauge-field">
          <label>Unit</label>
          <unit-picker .value=${d.unit} .select=${(v: string) => setGaugeDial(i, { unit: v })}></unit-picker>
        </div>
        <div class="gauge-field">
          <label>Decimal places</label>
          <plain-select .value=${String(d.decimals)} .options=${DECIMALS}
            .select=${(v: string) => setGaugeDial(i, { decimals: Number(v) })}></plain-select>
        </div>
      </div>
      <div class="gauge-sub">Scale</div>
      <div class="gauge-pair">
        <div class="gauge-field">
          <label for="gauge-min-${i}">Minimum</label>
          <input type="number" step="any" id="gauge-min-${i}" placeholder=${range && d.min == null && known ? `Auto (${fmt(range.min)})` : 'Auto'}
            .value=${d.min == null ? '' : String(d.min)}
            @input=${(e: Event) => setGaugeDial(i, { min: parseNum((e.target as HTMLInputElement).value) })} />
        </div>
        <div class="gauge-field">
          <label for="gauge-max-${i}">Maximum</label>
          <input type="number" step="any" id="gauge-max-${i}" placeholder=${range && d.max == null && known ? `Auto (${fmt(range.max)})` : 'Auto'}
            .value=${d.max == null ? '' : String(d.max)}
            @input=${(e: Event) => setGaugeDial(i, { max: parseNum((e.target as HTMLInputElement).value) })} />
        </div>
      </div>
      <div class="gauge-field">
        <label>Steps</label>
        <plain-select .value=${d.steps == null ? '' : String(d.steps)} .options=${stepOptions}
          .select=${(v: string) => setGaugeDial(i, { steps: v ? Number(v) : null })}></plain-select>
        <div class="toggle-hint ${range ? '' : 'is-error'}">${!range
          ? 'Maximum must be above minimum.'
          : scale
            ? `Numbers every ${new Intl.NumberFormat('en-US', { maximumFractionDigits: Math.min(decimalsOf(scale.step), 4) }).format(scale.step)}${unit}.`
            : 'Empty ends follow the data: from 0, to a round number above the highest reading.'}</div>
      </div>
      <div class="gauge-sub">Zones</div>
      ${d.zones.map((z, j) => this.renderZone(i, d, z, j))}
      <button class="btn btn-secondary gauge-add" @click=${addZone}>+ Add zone</button>
      <div class="toggle-hint">An empty end runs to the end of the scale. Where zones overlap, red wins.</div>
    </div>`;
  }

  private renderDial(d: GaugeDialState, i: number, count: number): TemplateResult {
    const open = this.open === i;
    return html`<div class="gauge-dial ${open ? 'is-open' : ''}">
      <div class="gauge-dial-head" role="button" tabindex="0" aria-expanded=${open ? 'true' : 'false'}
        @click=${() => { this.open = open ? -1 : i; }}
        @keydown=${(e: KeyboardEvent) => {
          if (e.target !== e.currentTarget) return;
          if (this.onMoveKey(e, i, count)) return;
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          this.open = open ? -1 : i;
        }}>
        ${count > 1
          ? html`<span class="chip-drag gauge-grip" title="Drag to reorder (Alt+↑/↓ from the keyboard)"
              @click=${(e: Event) => e.stopPropagation()}
              @pointerdown=${(e: PointerEvent) => this.onGrab(e, i)}>≡</span>`
          : nothing}
        <span class="gauge-dial-name">
          <span class="gauge-dial-title">${dialTitle(d)}</span>
          ${this.summary(d, i)}
        </span>
        ${count < GAUGE_MAX_DIALS
          ? html`<button class="gauge-dial-dup" title="Duplicate this dial, settings and all" aria-label="Duplicate dial"
              @click=${(e: Event) => {
                e.stopPropagation();
                duplicateGaugeDial(i);
                this.open = i + 1;
              }}><svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.3">
                <rect x="4.5" y="4.5" width="8" height="8" rx="1.2" /><path d="M9.5 2.5V2a.9.9 0 0 0-.9-.9H2.4A.9.9 0 0 0 1.5 2v6.2c0 .5.4.9.9.9h.6" />
              </svg></button>`
          : nothing}
        ${count > 1
          ? html`<button class="remove-btn" title="Remove this dial" @click=${(e: Event) => {
              e.stopPropagation();
              if (this.open === i) this.open = -1;
              else if (this.open > i) this.open--;
              removeGaugeDial(i);
            }}>×</button>`
          : nothing}
      </div>
      ${open ? this.renderBody(d, i) : nothing}
    </div>`;
  }

  override render(): TemplateResult {
    const dials = plotState.value.gauge_dials;
    return html`<div class="axis-block">
      <label>Dials <span class="toggle-hint">${dials.length > 1 ? `${dials.length} of ${GAUGE_MAX_DIALS} · drag to reorder` : 'one per sensor'}</span></label>
      <div class="gauge-dials">${dials.map((d, i) => this.renderDial(d, i, dials.length))}</div>
      ${dials.length < GAUGE_MAX_DIALS
        ? html`<button class="btn btn-secondary gauge-add" @click=${() => {
            addGaugeDial();
            this.open = dials.length;
          }}>+ Add dial</button>`
        : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gauge-controls': GaugeControls;
  }
}
